// GlobalMessage.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import MessageAlert from '../../ui/components/MessageAlert';
import { useUser } from '../../backend/useUser';
import { useCreditsContext } from './CreditsContext';
import { captureEvent } from '../../utils/analytics';
import { useLocation } from 'react-router-dom';
import { CREDITS_CONSTANTS, useCredits } from '../../backend/useCredits';

const GlobalMessageContainer = styled.div`
  position: absolute;
  top: 5px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 200;
`;

interface GlobalMessageProps {
  config: any;
  colorMode?: 'dark' | 'light';
}

const GlobalMessage: React.FC<GlobalMessageProps> = ({ config, colorMode = 'light' }) => {
  const location = useLocation();
  const currentPage = location.pathname;
  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [isRateLimitModalOpen, setIsRateLimitModalOpen] = useState(false);
  const [isCreditGiftModalOpen, setIsCreditGiftModalOpen] = useState(false);
  const [actionName, setActionName] = useState('');
  const { user } = useUser();
  const { isOutOfCredits, subscription, credits, isFreeTrialModalOpen } = useCreditsContext();
  const { checkShowFreeDailyGiftMessage } = useCredits();

  const upgradeMessage = config.ENABLE_FREE_TRIAL ? 
    `Start a free trial` : 
    `Choose a plan`;

  const isArtOrAnimate = currentPage.startsWith('/art') || currentPage.startsWith('/3d') || currentPage.startsWith('/animate');

  const extraMessage = isArtOrAnimate ? 
    (config.HARD_PAYWALL ? `${upgradeMessage}.` : 
      `${upgradeMessage} or check back tomorrow for free daily credits.`) :
    (config.HARD_PAYWALL ? 
      `${upgradeMessage} or play community games.` : 
      `${upgradeMessage} or check back tomorrow for free daily credits.`);

  const rateLimitMessageContent = {
    free: {
      key: 'free',
      title: 'Low Credits',
      body: `Not enough credits to ${actionName}. ${extraMessage}`
    },
    hobby: {
      key: 'hobby',
      title: 'Low Credits',
      body: `Not enough credits to ${actionName}. ${extraMessage}`
    },
    creator: {
      key: 'creator',
      title: 'Low Credits',
      body: 'Reach out to the playmix.ai team in discord about upgrading for additional credits.'
    }
  };

  const creditGiftMessageContent = {
    title: 'Free Daily Credits! 🎊',
    body: `You've received ${CREDITS_CONSTANTS.CREDIT_GIFTS.FREE_DAILY_GIFT} free gift credits. Enjoy creating!`
  };

  useEffect(() => {
    if (subscription) {
      setUserPlan(Object.keys(rateLimitMessageContent).find(key => rateLimitMessageContent[key].key === subscription));
    }
  }, [subscription]);

  // useEffect(() => {
  //   console.log('user plan', userPlan);
  // }, [userPlan]);

  // useEffect(() => {
  //   console.log('is out of credits', isOutOfCredits);
  // }, [isOutOfCredits]);

  // Determine which limit type to check based on the current URL path
  const shouldShowRateLimit = () => {
    // Check if we're on a game creation page
    if (currentPage === '/game') {
      setActionName('create a new game with AI');
      return isOutOfCredits.game_create_limited;
    }
    // Check if we're on a game edit page
    else if (currentPage.startsWith('/game/')) {
      setActionName('edit a game with AI');
      return isOutOfCredits.game_edit_limited;
    }
    // Check if we're on an art page
    else if (currentPage === '/art') {
      setActionName('create art with AI');
      return isOutOfCredits.art_2d_limited;
    }
    // Check if we're on a 2D art page
    else if (currentPage.startsWith('/art/')) {
      setActionName('create 2D art with AI');
      return isOutOfCredits.art_2d_limited;
    }
    // Check if we're on a 3D art page
    else if (currentPage.startsWith('/3d')) {
      setActionName('create 3D art with AI');
      return isOutOfCredits.art_3d_limited;
    }
    // Check if we're on animate root
    else if (currentPage === '/animate') {
      setActionName('animate with AI');
      return isOutOfCredits.animate_limited;
    }
    // Check if we're on an animate page
    else if (currentPage.startsWith('/animate/')) {
      setActionName('create animations with AI');
      return isOutOfCredits.animate_limited;
    }
    // Default to the overall limit for other pages
    else {
      setActionName('use AI features');
      return isOutOfCredits.is_limited_overall;
    }
  };

  // Check for rate limit changes
  useEffect(() => {
    if (shouldShowRateLimit()) {
      setIsRateLimitModalOpen(true);
    } else {
      // Close the modal if user is no longer out of credits
      setIsRateLimitModalOpen(false);
    }
  }, [isOutOfCredits, currentPage, credits]);

  // Check for free daily gift eligibility
  useEffect(() => {
    const checkDailyGiftEligibility = async () => {
      if (user?.id) {
        const isEligibleForDailyGift = await checkShowFreeDailyGiftMessage(user.id, config.HARD_PAYWALL);
        if (isEligibleForDailyGift) {
          setIsCreditGiftModalOpen(true);
        }
      }
    };
    
    checkDailyGiftEligibility();
  }, [user, checkShowFreeDailyGiftMessage]);

  const onUpgradeClick = (user) => {
    // console.log('upgrade clicked');
    window.open('/pricing', '_blank');
  };
  
  // Do not render if the free trial modal is open
  if (isFreeTrialModalOpen) {
    return null;
  }

  return (
    <GlobalMessageContainer>
      {isCreditGiftModalOpen ? (
        <MessageAlert
          titleText={creditGiftMessageContent.title}
          bodyText={creditGiftMessageContent.body}
          color='green'
          includeClose={true}
          onClose={() => setIsCreditGiftModalOpen(false)}
        />
      ) : isRateLimitModalOpen ? (
        <MessageAlert
          titleText={rateLimitMessageContent[userPlan || 'free'].title}
          bodyText={rateLimitMessageContent[userPlan || 'free'].body}
          color='red'
          primaryButton={userPlan !== 'creator' ? {
            text: 'Upgrade',
            onClick: () => onUpgradeClick(user),
          } : null}
          includeClose={true}
          onClose={() => setIsRateLimitModalOpen(false)}
        />
      ) : null}  
    </GlobalMessageContainer>
  );
};

export default GlobalMessage;